import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { NoteStatsMap } from '@/lib/exercise/engine';
import {
  buildRhythmExercise,
  emptyRhythmScore,
  isHoldLongEnough,
  rhythmAccuracy,
  scoreDelta,
  type ExerciseSummary,
  type RhythmConfig,
  type RhythmEvent,
  type RhythmExercise,
  type RhythmScore,
} from '@/lib/exercise/rhythmEngine';
import { getNote } from '@/lib/music/notes';
import {
  getAudioContext,
  playFeedback,
  playMidi,
  playMidiAt,
  resumeAudio,
  scheduleClickAt,
} from '@/lib/audio/synth';

export type RhythmPhase = 'idle' | 'playing' | 'paused' | 'done';

export interface RhythmFeedback {
  type: 'correct' | 'wrong' | 'warn';
  message: string;
}

export type RhythmMark = 'correct' | 'wrong' | 'warn';

interface RunState {
  exercise: RhythmExercise;
  beatDur: number;
  countInBeats: number;
  startTime: number;
  pausedSeconds: number;
  totalBeats: number;
  totalClicks: number;
  nextClick: number;
  /** eventos já avaliados (certo, errado ou cedo/atrasado) */
  graded: Set<number>;
  /**
   * eventos de nota cuja ALTURA certa já foi atacada (mesmo fora do tempo).
   * Serve para validar a ligadura: só damos crédito à continuação se a
   * nota-base realmente soou.
   */
  sounded: Set<number>;
}

interface Options {
  noteStats?: NoteStatsMap;
  onNoteResult?: (noteId: string, correct: boolean) => void;
  onFinish?: (score: RhythmScore, history: ExerciseSummary[]) => void;
  /** chamado quando a aula é reiniciada com exercícios já em andamento */
  onAbort?: (score: RhythmScore, history: ExerciseSummary[]) => void;
}

/** janela, em pulsos, para associar um toque a um evento */
const GRADED_WINDOW = 0.65;
/** tolerância, em pulsos, para considerar o toque "no pulso" */
const ON_TIME_TOLERANCE = 0.42;
/** pulsos de silêncio após o último evento antes de concluir o exercício */
const TAIL_BEATS = 1;

let seedCounter = 0;
function nextSeed(): number {
  seedCounter += 1;
  return Date.now() + seedCounter * 7919;
}

/** Índice inicial do "grupo de som" (nota-base + ligaduras encadeadas). */
function groupStartOf(exercise: RhythmExercise, index: number): number {
  let start = index;
  while (start > 0) {
    const event = exercise.events[start];
    if (event.kind === 'note' && event.tied === true) start -= 1;
    else break;
  }
  return start;
}

/** Índice final do grupo de som que começa em `start`. */
function groupEndOf(exercise: RhythmExercise, start: number): number {
  let end = start;
  while (end + 1 < exercise.events.length) {
    const next = exercise.events[end + 1];
    if (next.kind === 'note' && next.tied === true) end += 1;
    else break;
  }
  return end;
}

/** Duração total (em pulsos) que o grupo de som precisa ser sustentado. */
function groupBeats(exercise: RhythmExercise, start: number): number {
  const first = exercise.events[start];
  if (!first || first.kind !== 'note') return 0;
  let beats = first.beats;
  let i = start + 1;
  while (i < exercise.events.length) {
    const event = exercise.events[i];
    if (event.kind === 'note' && event.tied === true) {
      beats += event.beats;
      i += 1;
    } else break;
  }
  return beats;
}

export function useRhythmSession(config: RhythmConfig, options: Options = {}) {
  const [phase, setPhaseState] = useState<RhythmPhase>('idle');
  const [exercise, setExercise] = useState<RhythmExercise>(() => buildRhythmExercise(config, nextSeed()));
  const [exerciseIndex, setExerciseIndex] = useState(0);
  const [score, setScore] = useState<RhythmScore>(emptyRhythmScore);
  const [history, setHistory] = useState<ExerciseSummary[]>([]);
  const [feedback, setFeedback] = useState<RhythmFeedback | null>(null);
  const [marks, setMarks] = useState<Record<number, RhythmMark>>({});
  const [currentEventIndex, setCurrentEventIndex] = useState(-1);
  const [currentBeat, setCurrentBeat] = useState(-1);
  const [countInBeat, setCountInBeat] = useState(0);
  const [bpm, setBpmState] = useState(config.bpm);
  const [slowMode, setSlowModeState] = useState(false);

  const effectiveBpm = useMemo(
    () => (slowMode ? Math.max(40, Math.round(bpm / 2)) : bpm),
    [bpm, slowMode],
  );

  const phaseRef = useRef<RhythmPhase>('idle');
  const configRef = useRef(config);
  const runRef = useRef<RunState | null>(null);
  const exerciseIndexRef = useRef(0);
  const scoreRef = useRef<RhythmScore>(emptyRhythmScore());
  /** placar no início do exercício atual, para isolar o que aconteceu nele */
  const startScoreRef = useRef<RhythmScore>(emptyRhythmScore());
  const historyRef = useRef<ExerciseSummary[]>([]);
  const effectiveBpmRef = useRef(effectiveBpm);
  const noteStatsRef = useRef(options.noteStats);
  const onNoteResultRef = useRef(options.onNoteResult);
  const onFinishRef = useRef(options.onFinish);
  const onAbortRef = useRef(options.onAbort);
  const rafRef = useRef<number | null>(null);
  const timerRef = useRef<number | null>(null);
  const transitionRef = useRef<number | null>(null);
  const cancelsRef = useRef<(() => void)[]>([]);
  const aliveRef = useRef(true);
  const lastEventRef = useRef(-1);
  const lastBeatRef = useRef(-1);
  const lastCountInRef = useRef(-1);
  const startExerciseRef = useRef<((ex: RhythmExercise, index: number) => void) | null>(null);
  /** instante (em segundos de áudio) em que a nota-base de um grupo foi atacada */
  const holdsRef = useRef<Map<number, number>>(new Map());
  /** grupos cuja duração já foi julgada (evita contar duas vezes) */
  const durationDoneRef = useRef<Set<number>>(new Set());
  /** grupos cuja duração foi considerada errada */
  const brokenGroupsRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    configRef.current = config;
    noteStatsRef.current = options.noteStats;
    onNoteResultRef.current = options.onNoteResult;
    onFinishRef.current = options.onFinish;
    onAbortRef.current = options.onAbort;
  });

  useEffect(() => {
    effectiveBpmRef.current = effectiveBpm;
  }, [effectiveBpm]);

  const updatePhase = useCallback((next: RhythmPhase) => {
    phaseRef.current = next;
    setPhaseState(next);
  }, []);

  const resetHoldState = useCallback(() => {
    holdsRef.current.clear();
    durationDoneRef.current.clear();
    brokenGroupsRef.current.clear();
  }, []);

  /**
   * Monta a receita de cada exercício. Em aulas que comparam compassos, a
   * fórmula (2/4, 3/4, 4/4...) roda conforme o índice do exercício.
   */
  const resolveConfig = useCallback((index: number): RhythmConfig => {
    const base = configRef.current;
    const signatures = base.timeSignatures;
    if (!signatures || signatures.length === 0) return base;
    return { ...base, beatsPerMeasure: signatures[index % signatures.length] };
  }, []);

  const nowSec = useCallback((): number => {
    const ctx = getAudioContext();
    return ctx ? ctx.currentTime : performance.now() / 1000;
  }, []);

  const bump = useCallback((key: keyof RhythmScore, countAttempt = true) => {
    const next = { ...scoreRef.current };
    next[key] += 1;
    if (countAttempt && key !== 'attempts') next.attempts += 1;
    scoreRef.current = next;
    setScore(next);
  }, []);

  /**
   * Julga a DURAÇÃO de um grupo de som (nota-base + ligaduras).
   *   - 'held'      → a tecla foi solta e ainda estava dentro da tolerância
   *                   (medição explícita pelo fim da nota).
   *   - 'heldToEnd' → a tecla ainda estava pressionada quando a figura terminou;
   *                   vira acerto só se o aparelho comprovar que informa o fim
   *                   da nota (senão fica NÃO AVALIADA).
   *   - 'short'     → a tecla foi solta cedo ou um novo toque cortou a figura.
   * A duração não entra no cálculo de precisão (altura/ataque); é reportada em
   * separado.
   */
  const finalizeHold = useCallback(
    (startIndex: number, status: 'held' | 'heldToEnd' | 'short') => {
      if (durationDoneRef.current.has(startIndex)) return;
      durationDoneRef.current.add(startIndex);
      holdsRef.current.delete(startIndex);
      if (status === 'short') {
        brokenGroupsRef.current.add(startIndex);
        bump('durationBreak', false);
      } else if (status === 'held') {
        // medição explícita: a tecla foi solta e ainda estava dentro da tolerância
        bump('durationsHeld', false);
      } else {
        // continuava pressionada quando a figura terminou — só vira acerto se
        // o aparelho comprovar que informa o fim da nota (ver buildRhythmSummary)
        bump('heldToEnd', false);
      }
    },
    [bump],
  );

  const cancelClicks = useCallback(() => {
    cancelsRef.current.forEach((cancel) => cancel());
    cancelsRef.current = [];
  }, []);

  const stopTimers = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (rafRef.current !== null) {
      window.cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  /**
   * Dá crédito a um evento de sustentação (pausa ou continuação de ligadura)
   * quando o aluno o respeitou — isto é, NÃO atacou a nota no lugar errado.
   *
   * Regras de avaliação correta:
   * - pausa: crédito se ficou em silêncio (nenhum ataque dentro dela);
   * - ligadura: crédito SÓ se a nota-base (a que foi realmente atacada) soou
   *   com a altura certa e a sua duração NÃO foi quebrada.
   */
  const creditHold = useCallback(
    (run: RunState, index: number, event: RhythmEvent): boolean => {
      if (run.graded.has(index)) return false;
      if (event.kind === 'rest') {
        run.graded.add(index);
        setMarks((current) => ({ ...current, [index]: 'correct' }));
        setFeedback({ type: 'correct', message: 'Silêncio certo: você respeitou a pausa.' });
        bump('correct');
        bump('restsHeld', false);
        return true;
      }
      if (event.kind === 'note' && event.tied === true) {
        const baseIndex = index - 1;
        const base = baseIndex >= 0 ? run.exercise.events[baseIndex] : null;
        const baseStart = baseIndex >= 0 ? groupStartOf(run.exercise, baseIndex) : -1;
        const baseOk =
          base !== null &&
          base.kind === 'note' &&
          !base.tied &&
          run.sounded.has(baseIndex) &&
          !brokenGroupsRef.current.has(baseStart);
        if (!baseOk) return false;
        run.graded.add(index);
        setMarks((current) => ({ ...current, [index]: 'correct' }));
        setFeedback({ type: 'correct', message: 'Ligadura certa: você segurou o som.' });
        bump('correct');
        bump('tiesHeld', false);
        return true;
      }
      return false;
    },
    [bump],
  );

  /**
   * Fecha a duração de um grupo quando o tempo dele termina sem que a tecla
   * tenha sido solta antes. Se a tecla continua pressionada até o fim da
   * figura, a nota foi sustentada por inteiro.
   */
  const finalizeGroupEnd = useCallback(
    (run: RunState, previous: number) => {
      const event = run.exercise.events[previous];
      if (!event || event.kind !== 'note') return;
      const start = groupStartOf(run.exercise, previous);
      const end = groupEndOf(run.exercise, start);
      if (end !== previous) return;
      if (!holdsRef.current.has(start)) return;
      finalizeHold(start, 'heldToEnd');
    },
    [finalizeHold],
  );

  /**
   * Avaliação final do exercício. Garante que eventos de sustentação que
   * escaparam da checagem por quadro sejam julgados — em especial o ÚLTIMO
   * evento — e que toda duração ainda pendente (nota segurada até o fim) seja
   * fechada como correta.
   */
  const finalizeRemaining = useCallback(() => {
    const run = runRef.current;
    if (!run) return;
    run.exercise.events.forEach((event, index) => {
      if (run.graded.has(index)) return;
      if (event.kind === 'note' && event.tied !== true) {
        // Nota escrita que nunca foi atacada: sem altura, sem ataque. Conta
        // como erro de nota — não se pode "passar batido" pelos eventos.
        bump('missedNotes', false);
        return;
      }
      creditHold(run, index, event);
    });
    Array.from(holdsRef.current.keys()).forEach((start) => finalizeHold(start, 'heldToEnd'));
  }, [bump, creditHold, finalizeHold]);

  const finishExercise = useCallback(() => {
    finalizeRemaining();
    const finished = runRef.current;
    const beatsPerMeasure =
      finished?.exercise.beatsPerMeasure ?? configRef.current.beatsPerMeasure;
    // Registra o que foi avaliado NESTE exercício (diferença em relação ao início).
    const entry: ExerciseSummary = {
      index: exerciseIndexRef.current,
      timeSignature: `${beatsPerMeasure}/4`,
      score: scoreDelta(startScoreRef.current, scoreRef.current),
    };
    historyRef.current = [...historyRef.current, entry];
    setHistory(historyRef.current);
    stopTimers();
    cancelClicks();
    runRef.current = null;
    if (exerciseIndexRef.current + 1 >= configRef.current.exercises) {
      updatePhase('done');
      onFinishRef.current?.(scoreRef.current, historyRef.current);
      return;
    }
    setFeedback({ type: 'correct', message: 'Exercício concluído. Preparando o próximo...' });
    const nextIndex = exerciseIndexRef.current + 1;
    transitionRef.current = window.setTimeout(() => {
      if (!aliveRef.current) return;
      const nextExercise = buildRhythmExercise(resolveConfig(nextIndex), nextSeed(), noteStatsRef.current);
      startExerciseRef.current?.(nextExercise, nextIndex);
    }, 1100);
  }, [cancelClicks, finalizeRemaining, resolveConfig, stopTimers, updatePhase]);

  const scheduleAhead = useCallback(() => {
    const run = runRef.current;
    const ctx = getAudioContext();
    if (!run || !ctx) return;
    const horizon = ctx.currentTime + 0.18;
    while (run.nextClick < run.totalClicks && run.startTime + run.nextClick * run.beatDur < horizon) {
      const time = run.startTime + run.nextClick * run.beatDur;
      const accent = run.nextClick % run.exercise.beatsPerMeasure === 0;
      cancelsRef.current.push(scheduleClickAt(time, accent));
      run.nextClick += 1;
    }
    if (cancelsRef.current.length > 48) {
      cancelsRef.current = cancelsRef.current.slice(-24);
    }
  }, []);

  const loop = useCallback(() => {
    const run = runRef.current;
    if (!run) return;
    const elapsedBeats = (nowSec() - run.startTime) / run.beatDur;
    const exBeat = elapsedBeats - run.countInBeats;

    if (exBeat < 0) {
      const ci = Math.max(1, Math.floor(elapsedBeats) + 1);
      if (ci !== lastCountInRef.current) {
        lastCountInRef.current = ci;
        setCountInBeat(ci);
      }
      if (lastEventRef.current !== -1) {
        lastEventRef.current = -1;
        setCurrentEventIndex(-1);
      }
      if (lastBeatRef.current !== -1) {
        lastBeatRef.current = -1;
        setCurrentBeat(-1);
      }
    } else {
      if (lastCountInRef.current !== 0) {
        lastCountInRef.current = 0;
        setCountInBeat(0);
      }
      const beat = (Math.floor(exBeat) % run.exercise.beatsPerMeasure) + 1;
      if (beat !== lastBeatRef.current) {
        lastBeatRef.current = beat;
        setCurrentBeat(beat);
      }
      const idx = run.exercise.events.findIndex(
        (event) => exBeat >= event.startBeat && exBeat < event.startBeat + event.beats,
      );
      if (idx !== lastEventRef.current) {
        const previous = lastEventRef.current;
        // Ficou em silêncio na pausa (ou segurou a ligadura)? Isso é acerto.
        if (previous >= 0 && !run.graded.has(previous)) {
          creditHold(run, previous, run.exercise.events[previous]);
        }
        // Fechou a figura ainda segurando a tecla? Duração correta.
        if (previous >= 0) finalizeGroupEnd(run, previous);
        lastEventRef.current = idx;
        setCurrentEventIndex(idx);
      }
    }

    if (exBeat >= run.totalBeats + TAIL_BEATS) {
      finishExercise();
      return;
    }
    rafRef.current = window.requestAnimationFrame(loop);
  }, [creditHold, finalizeGroupEnd, finishExercise, nowSec]);

  const startExercise = useCallback(
    (nextExercise: RhythmExercise, index: number) => {
      stopTimers();
      cancelClicks();
      if (transitionRef.current !== null) {
        window.clearTimeout(transitionRef.current);
        transitionRef.current = null;
      }
      resumeAudio();
      resetHoldState();
      const beatDur = 60 / effectiveBpmRef.current;
      const countInBeats = configRef.current.countIn ?? nextExercise.beatsPerMeasure;
      const startTime = nowSec() + 0.4;
      runRef.current = {
        exercise: nextExercise,
        beatDur,
        countInBeats,
        startTime,
        pausedSeconds: 0,
        totalBeats: nextExercise.totalBeats,
        totalClicks: countInBeats + Math.round(nextExercise.totalBeats),
        nextClick: 0,
        graded: new Set<number>(),
        sounded: new Set<number>(),
      };
      exerciseIndexRef.current = index;
      startScoreRef.current = { ...scoreRef.current };
      setExerciseIndex(index);
      setExercise(nextExercise);
      setMarks({});
      setFeedback(null);
      setCurrentEventIndex(-1);
      setCurrentBeat(-1);
      setCountInBeat(0);
      lastEventRef.current = -1;
      lastBeatRef.current = -1;
      lastCountInRef.current = 0;
      updatePhase('playing');
      scheduleAhead();
      timerRef.current = window.setInterval(scheduleAhead, 25);
      rafRef.current = window.requestAnimationFrame(loop);
    },
    [cancelClicks, loop, nowSec, resetHoldState, scheduleAhead, stopTimers, updatePhase],
  );

  useEffect(() => {
    startExerciseRef.current = startExercise;
  }, [startExercise]);

  const startLesson = useCallback(() => {
    // Reiniciar com exercícios em andamento = tentativa incompleta.
    if (phaseRef.current !== 'done' && scoreRef.current.attempts > 0) {
      onAbortRef.current?.(scoreRef.current, historyRef.current);
    }
    scoreRef.current = emptyRhythmScore();
    setScore(emptyRhythmScore());
    historyRef.current = [];
    setHistory([]);
    const first = buildRhythmExercise(resolveConfig(0), nextSeed(), noteStatsRef.current);
    startExercise(first, 0);
  }, [resolveConfig, startExercise]);

  const pause = useCallback(() => {
    const run = runRef.current;
    if (!run || phaseRef.current !== 'playing') return;
    // Fecha as durações pendentes: a nota estava sustentada até a pausa.
    Array.from(holdsRef.current.keys()).forEach((start) => finalizeHold(start, 'heldToEnd'));
    run.pausedSeconds = nowSec() - run.startTime;
    stopTimers();
    cancelClicks();
    updatePhase('paused');
    setCurrentEventIndex(-1);
    setCurrentBeat(-1);
    setCountInBeat(0);
    lastEventRef.current = -1;
    lastBeatRef.current = -1;
    lastCountInRef.current = -1;
  }, [cancelClicks, finalizeHold, nowSec, stopTimers, updatePhase]);

  const resume = useCallback(() => {
    const run = runRef.current;
    const ctx = getAudioContext();
    if (!run || !ctx || phaseRef.current !== 'paused') return;
    run.beatDur = 60 / effectiveBpmRef.current;
    run.startTime = ctx.currentTime - run.pausedSeconds + 0.15;
    run.nextClick = Math.max(0, Math.ceil(run.pausedSeconds / run.beatDur));
    resumeAudio();
    updatePhase('playing');
    scheduleAhead();
    timerRef.current = window.setInterval(scheduleAhead, 25);
    rafRef.current = window.requestAnimationFrame(loop);
  }, [loop, scheduleAhead, updatePhase]);

  const restartExercise = useCallback(() => {
    const run = runRef.current;
    if (run) {
      startExercise(run.exercise, exerciseIndexRef.current);
    } else {
      startExercise(
        buildRhythmExercise(resolveConfig(exerciseIndexRef.current), nextSeed(), noteStatsRef.current),
        exerciseIndexRef.current,
      );
    }
  }, [resolveConfig, startExercise]);

  const togglePrimary = useCallback(() => {
    if (phaseRef.current === 'playing') {
      pause();
      return;
    }
    if (phaseRef.current === 'paused') {
      resume();
      return;
    }
    startLesson();
  }, [pause, resume, startLesson]);

  const handleKey = useCallback(
    (midi: number) => {
      playMidi(midi, 0.5);
      const run = runRef.current;
      if (!run || phaseRef.current !== 'playing') return;

      const exBeat = (nowSec() - run.startTime) / run.beatDur - run.countInBeats;
      if (exBeat < -0.45) {
        setFeedback({ type: 'warn', message: 'Aguarde a contagem inicial antes de tocar.' });
        return;
      }

      const pitchMatches = (noteId: string): boolean =>
        configRef.current.responseMode === 'pulse' || midi === getNote(noteId).midi;

      // 1) Evento não avaliado cujo início está mais perto do toque.
      let bestIndex = -1;
      let bestAbs = Infinity;
      let bestDelta = 0;
      run.exercise.events.forEach((event, index) => {
        if (run.graded.has(index)) return;
        const delta = exBeat - event.startBeat;
        const abs = Math.abs(delta);
        if (abs <= GRADED_WINDOW && abs < bestAbs) {
          bestAbs = abs;
          bestIndex = index;
          bestDelta = delta;
        }
      });

      // 2) Sem evento perto do início: vemos em qual evento o toque realmente
      //    caiu. Assim, uma nota tocada no MEIO de uma pausa longa (ou sobre a
      //    ligadura) é reconhecida como intrusa em vez de passar batido — antes
      //    ela não contava contra e a pausa ainda ganhava o crédito de silêncio.
      if (bestIndex < 0) {
        const currentIdx = run.exercise.events.findIndex(
          (event) => exBeat >= event.startBeat && exBeat < event.startBeat + event.beats,
        );
        if (currentIdx >= 0 && !run.graded.has(currentIdx)) {
          const currentEvent = run.exercise.events[currentIdx];
          run.graded.add(currentIdx);
          if (
            currentEvent.kind === 'rest' ||
            (currentEvent.kind === 'note' && currentEvent.tied === true)
          ) {
            setMarks((current) => ({ ...current, [currentIdx]: 'wrong' }));
            setFeedback({
              type: 'wrong',
              message:
                currentEvent.kind === 'rest'
                  ? 'Durante a pausa, o certo é o silêncio.'
                  : 'A nota está ligada: segure o som, não ataque de novo.',
            });
            bump('silence');
            if (currentEvent.kind === 'rest') bump('restIntrusion');
            else bump('tieReattack');
            playFeedback(false);
            return;
          }
          if (!pitchMatches(currentEvent.noteId)) {
            setMarks((current) => ({ ...current, [currentIdx]: 'wrong' }));
            setFeedback({
              type: 'wrong',
              message: `Nota errada. Era ${getNote(currentEvent.noteId).label}.`,
            });
            bump('wrongNote');
            playFeedback(false);
            onNoteResultRef.current?.(currentEvent.noteId, false);
            return;
          }
          // Altura certa, mas o início já passou: ataque atrasado.
          run.sounded.add(currentIdx);
          setMarks((current) => ({ ...current, [currentIdx]: 'warn' }));
          setFeedback({ type: 'warn', message: 'Nota certa, mas um pouco atrasada.' });
          bump('late');
          if (configRef.current.responseMode === 'on-beat') {
            holdsRef.current.set(currentIdx, nowSec());
          }
          return;
        }
        // Toque dentro de um evento JÁ avaliado: se era uma nota comum, o
        // segundo toque corta a figura antes do fim da sua duração.
        if (currentIdx >= 0) {
          const gradedEvent = run.exercise.events[currentIdx];
          if (gradedEvent.kind === 'note' && gradedEvent.tied !== true) {
            finalizeHold(groupStartOf(run.exercise, currentIdx), 'short');
            setFeedback({
              type: 'wrong',
              message:
                'Você tocou de novo antes de a figura terminar. Deixe a nota soar por toda a sua duração.',
            });
            playFeedback(false);
            return;
          }
        }
        setFeedback({ type: 'warn', message: 'Fora do tempo. Siga o metrônomo e aguarde o pulso.' });
        return;
      }

      const event = run.exercise.events[bestIndex];
      run.graded.add(bestIndex);

      if (event.kind === 'rest' || (event.kind === 'note' && event.tied === true)) {
        setMarks((current) => ({ ...current, [bestIndex]: 'wrong' }));
        setFeedback({
          type: 'wrong',
          message:
            event.kind === 'rest'
              ? 'Durante a pausa, o certo é o silêncio.'
              : 'A nota está ligada: segure o som, não ataque de novo.',
        });
        bump('silence');
        if (event.kind === 'rest') bump('restIntrusion');
        else bump('tieReattack');
        playFeedback(false);
        return;
      }

      if (!pitchMatches(event.noteId)) {
        setMarks((current) => ({ ...current, [bestIndex]: 'wrong' }));
        setFeedback({ type: 'wrong', message: `Nota errada. Era ${getNote(event.noteId).label}.` });
        bump('wrongNote');
        playFeedback(false);
        onNoteResultRef.current?.(event.noteId, false);
        return;
      }

      // A altura certa soou: a continuação de ligadura seguinte pode contar.
      run.sounded.add(bestIndex);
      // E começa a contagem da duração (medida ao soltar a tecla) — só quando a
      // aula exige sustentar a nota (modo "no pulso"); na marcação de pulso, não.
      if (configRef.current.responseMode === 'on-beat') {
        holdsRef.current.set(bestIndex, nowSec());
      }

      if (Math.abs(bestDelta) <= ON_TIME_TOLERANCE) {
        setMarks((current) => ({ ...current, [bestIndex]: 'correct' }));
        setFeedback({
          type: 'correct',
          message:
            configRef.current.responseMode === 'pulse'
              ? 'No pulso!'
              : `Certo! ${getNote(event.noteId).label} no tempo.`,
        });
        bump('correct');
        playFeedback(true);
        onNoteResultRef.current?.(event.noteId, true);
      } else if (bestDelta < 0) {
        setMarks((current) => ({ ...current, [bestIndex]: 'warn' }));
        setFeedback({ type: 'warn', message: 'Nota certa, mas um pouco cedo.' });
        bump('early');
      } else {
        setMarks((current) => ({ ...current, [bestIndex]: 'warn' }));
        setFeedback({ type: 'warn', message: 'Nota certa, mas um pouco atrasada.' });
        bump('late');
      }
    },
    [bump, finalizeHold, nowSec],
  );

  /**
   * Fim da nota: soltar a tecla (teclado virtual) ou Note Off (MIDI). Aqui
   * medimos quanto tempo a nota foi sustentada e julgamos a duração.
   */
  const handleKeyUp = useCallback(
    (midi: number) => {
      const run = runRef.current;
      if (!run || phaseRef.current !== 'playing') return;
      // Evidência de que o aparelho informa o fim da nota (soltar a tecla no
      // teclado virtual ou Note Off no MIDI). Sem isso, a duração fica NÃO
      // AVALIADA em vez de "correta".
      bump('noteOffCount', false);
      let target = -1;
      holdsRef.current.forEach((_start, start) => {
        if (durationDoneRef.current.has(start)) return;
        const event = run.exercise.events[start];
        if (event && event.kind === 'note' && event.tied !== true && getNote(event.noteId).midi === midi) {
          target = start;
        }
      });
      if (target < 0) return;
      const startSec = holdsRef.current.get(target);
      if (startSec === undefined) return;
      const required = groupBeats(run.exercise, target);
      const heldBeats = (nowSec() - startSec) / run.beatDur;
      const ok = isHoldLongEnough(heldBeats, required);
      finalizeHold(target, ok ? 'held' : 'short');
      if (!ok) {
        setFeedback({
          type: 'wrong',
          message: 'Você soltou a tecla antes do fim da figura. Segure a nota pela duração toda.',
        });
        playFeedback(false);
      }
    },
    [bump, finalizeHold, nowSec],
  );

  const playExample = useCallback(() => {
    const ctx = getAudioContext();
    if (!ctx) return;
    resumeAudio();
    const beatDur = 60 / effectiveBpmRef.current;
    const t0 = ctx.currentTime + 0.25;
    const events = (runRef.current?.exercise ?? exercise).events;
    let index = 0;
    while (index < events.length) {
      const event = events[index];
      if (event.kind !== 'note' || event.tied === true) {
        index += 1;
        continue;
      }
      // Uma nota ligada à seguinte soa como um único ataque prolongado.
      let heldBeats = event.beats;
      let next = index + 1;
      while (next < events.length) {
        const following = events[next];
        if (following.kind === 'note' && following.tied === true) {
          heldBeats += following.beats;
          next += 1;
        } else {
          break;
        }
      }
      playMidiAt(getNote(event.noteId).midi, t0 + event.startBeat * beatDur, heldBeats * beatDur * 0.92);
      index = next;
    }
  }, [exercise]);

  const setBpm = useCallback((value: number) => {
    setBpmState(value);
  }, []);

  const setSlowMode = useCallback((value: boolean) => {
    setSlowModeState(value);
  }, []);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      stopTimers();
      cancelClicks();
      if (transitionRef.current !== null) window.clearTimeout(transitionRef.current);
    };
  }, [cancelClicks, stopTimers]);

  return {
    phase,
    exercise,
    exerciseIndex,
    totalExercises: config.exercises,
    beatsPerMeasure: exercise.beatsPerMeasure,
    timeSignature: `${exercise.beatsPerMeasure}/4`,
    score,
    accuracy: rhythmAccuracy(score),
    history,
    feedback,
    marks,
    currentEventIndex,
    currentBeat,
    countInBeat,
    bpm,
    slowMode,
    effectiveBpm,
    responseMode: config.responseMode,
    start: startLesson,
    togglePrimary,
    pause,
    resume,
    restartExercise,
    handleKey,
    handleKeyUp,
    playExample,
    setBpm,
    setSlowMode,
  };
}